// ── Calendar colour maps depend on T (theme) ─────────────────────────────────
import { T } from "../theme.js";

// ── Cross-provider event deduplication ────────────────────────────────────────
// Two-pass dedup: (1) exact iCal UID + date, (2) fuzzy title + date + time.
// Higher-priority sources win when duplicates are found.
const PROVIDER_PRIO=['google','outlook','icloud','caldav','yahoo','ofw','orbit'];
const prio=(src)=>{const i=PROVIDER_PRIO.indexOf(src);return i===-1?PROVIDER_PRIO.length:i;};

export function crossProviderDedupe(evs){
  // Pass 1: exact UID match
  const bestByUid=new Map();
  for(const ev of evs){
    const uid=ev.iCalUID||ev.uid;
    if(!uid) continue;
    const key=`${uid}\x00${ev.date||''}`;
    const prev=bestByUid.get(key);
    if(!prev||prio(ev.source)<prio(prev.source)) bestByUid.set(key,ev);
  }
  const uidWinners=new Set([...bestByUid.values()].map(e=>e.id));
  const afterUid=evs.filter(ev=>{const uid=ev.iCalUID||ev.uid;return !uid||uidWinners.has(ev.id);});

  // Pass 2: fuzzy match by normalised title + date + time (rounded to 15 min)
  const bestByContent=new Map();
  for(const ev of afterUid){
    const title=(ev.label||'').trim().toLowerCase();
    if(!title||!ev.date) continue;
    const s=Math.round((ev.startH||0)*4);
    const e=Math.round((ev.endH||0)*4);
    const key=`${title}\x00${ev.date}\x00${s}\x00${e}\x00${ev.allDay||false}`;
    const prev=bestByContent.get(key);
    if(!prev||prio(ev.source)<prio(prev.source)) bestByContent.set(key,ev);
  }
  const contentWinners=new Set([...bestByContent.values()].map(e=>e.id));
  return afterUid.filter(ev=>{
    const title=(ev.label||'').trim().toLowerCase();
    if(!title||!ev.date) return true;
    const s=Math.round((ev.startH||0)*4);
    const e=Math.round((ev.endH||0)*4);
    const key=`${title}\x00${ev.date}\x00${s}\x00${e}\x00${ev.allDay||false}`;
    return contentWinners.has(ev.id);
  });
}

// ── Group Scheduling constants ────────────────────────────────────────────────
export const GS_HOUR_HEIGHT = 40;
export const GS_GRID_START  = 8;
export const GS_GRID_END    = 24;
export const GS_VISIBLE_HOURS = GS_GRID_END - GS_GRID_START;

export const GS_DAYS     = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
export const GS_DAY_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// ── Layout overlapping schedule events side-by-side ──────────────────────────
export function layoutScheduleEvents(evts){
  if(!evts.length) return [];
  const sorted=[...evts].sort((a,b)=>{
    if(a.startH!==b.startH) return a.startH-b.startH;
    const dA=a.endH-a.startH,dB=b.endH-b.startH;
    if(dA!==dB) return dB-dA;
    return (a.label||'').localeCompare(b.label||'');
  });
  const clusters=[];
  let cur=null;
  for(const ev of sorted){
    if(!cur||ev.startH>=cur.end){cur={events:[],end:-Infinity,cols:[]};clusters.push(cur);}
    let col=cur.cols.findIndex(endH=>endH<=ev.startH);
    if(col<0) col=cur.cols.length;
    cur.cols[col]=ev.endH;
    cur.end=Math.max(cur.end,ev.endH);
    cur.events.push({ev,col});
  }
  return clusters.flatMap(cl=>{
    const n=cl.cols.length;
    return cl.events.map(({ev,col})=>({...ev,_col:col,_totalCols:n}));
  });
}

// ── Real-date helpers (computed once at page load) ────────────────────────────
export const _CAL_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Returns "YYYY-MM-DD" using LOCAL date components (avoids UTC-offset day shifts from toISOString) */
export function toLocalYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export const _nowDate = new Date();
const _todayDow = _nowDate.getDay(); // JS: 0=Sun,1=Mon…6=Sat
export const TODAY_IDX = _todayDow;  // orbit: 0=Sun…6=Sat (matches JS getDay())

export const _sunday = new Date(_nowDate);
_sunday.setDate(_nowDate.getDate() - TODAY_IDX);
_sunday.setHours(0, 0, 0, 0);

export const WEEK_DATES     = Array.from({length:7}, (_,i) => { const d=new Date(_sunday); d.setDate(_sunday.getDate()+i); return d; });
export const WEEK_DATE_NUMS = WEEK_DATES.map(d => d.getDate());
export const WEEK_OF_STR    = `${_CAL_MONTHS[WEEK_DATES[0].getMonth()]} ${WEEK_DATES[0].getDate()}`;

export function fmtWeekDate(i) { return `${_CAL_MONTHS[WEEK_DATES[i].getMonth()]} ${WEEK_DATE_NUMS[i]}`; }

export const _DAY_NAMES_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
export const TODAY_DATE_STR = `${_DAY_NAMES_FULL[_nowDate.getDay()]}, ${_CAL_MONTHS[_nowDate.getMonth()]} ${_nowDate.getDate()}`;

export const WEEK_RANGE_STR = WEEK_DATES[0].getMonth() === WEEK_DATES[6].getMonth()
  ? `${_CAL_MONTHS[WEEK_DATES[0].getMonth()]} ${WEEK_DATES[0].getDate()}–${WEEK_DATES[6].getDate()}`
  : `${_CAL_MONTHS[WEEK_DATES[0].getMonth()]} ${WEEK_DATES[0].getDate()} – ${_CAL_MONTHS[WEEK_DATES[6].getMonth()]} ${WEEK_DATES[6].getDate()}`;

export const TODAY_FULL_STR = `${_CAL_MONTHS[_nowDate.getMonth()]} ${_nowDate.getDate()}, ${_nowDate.getFullYear()}`;

/** Convert a fractional hour value (e.g. 13.5) to an <input type="time"> string ("13:30") */
export const toTimeInput  = (h) => `${String(Math.floor(h)).padStart(2,"0")}:${h%1===0.5?"30":"00"}`;
/** Parse an <input type="time"> string back to a fractional hour */
export const fromTimeInput = (s) => { const [hh,mm]=s.split(":").map(Number); return hh+(mm>=30?0.5:0); };

export const GS_DATES = WEEK_DATE_NUMS;
export const GS_TODAY = TODAY_IDX;

// ── Home-screen static data (also used by PersonDrawer schedule tab) ─────────
export const HOME_EVENTS = [
  {day:0,personId:1,label:"Alex overnight",  start:18,end:24},
  {day:2,personId:7,label:"Priya coffee",    start:9, end:11},
  {day:3,personId:3,label:"Sam date",        start:19,end:22},
  {day:4,personId:9,label:"Devon – handoff", start:15,end:16},
  {day:5,personId:1,label:"Alex overnight",  start:14,end:24},
  {day:6,personId:5,label:"Mom – Sunday din",start:17,end:21},
];
export const HOME_NOTIFS = [
  {id:1,personId:1,text:"Alex confirmed tonight",         time:"2h ago",   action:null},
  {id:2,personId:2,text:"Jordan requested Sat Mar 1",     time:"Yesterday",action:"respond"},
  {id:4,personId:4,text:"River: fairness below target",   time:"3d ago",   action:"schedule"},
  {id:5,personId:9,text:"Devon updated custody calendar", time:"1h ago",   action:"view"},
  {id:6,personId:8,text:"Marcus: 21 days since you met",  time:"",         action:"reach-out"},
];
export const HOME_WEEK_DAYS  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
export const HOME_WEEK_DATES = WEEK_DATE_NUMS;
export const HOME_TODAY      = TODAY_IDX;

// ── Month-level constants ─────────────────────────────────────────────────────
const _now = new Date();
export const MONTH_DAYS         = new Date(_now.getFullYear(), _now.getMonth()+1, 0).getDate();
export const TODAY_DAY          = _now.getDate();
export const CURRENT_MONTH_NAME = _now.toLocaleString("default", {month:"long"});

// ── Group-scheduling computation helpers ──────────────────────────────────────
export function gsIntersect(a, b) {
  const out = [];
  for (const [as,ae] of a) for (const [bs,be] of b) { const s=Math.max(as,bs), e=Math.min(ae,be); if(e-s>=1) out.push([s,e]); }
  return out;
}

export function gsGroupOverlaps(ids, myAvail, allPeople, overrides={}) {
  const avail = myAvail || {};
  const r = {};
  for (let d=0; d<7; d++) {
    let slots = avail[d] || [];
    for (const id of ids) { const pAvail=overrides[id]||{}; slots=gsIntersect(slots, pAvail[d]||[]); if(!slots.length) break; }
    r[d] = slots;
  }
  return r;
}

export function gsHeatmap(ids, day, myAvail, allPeople, overrides={}) {
  const avail = myAvail || {};
  const counts = new Array(GS_VISIBLE_HOURS*2).fill(0);
  const total = ids.length + 1;
  for (let i=0; i<GS_VISIBLE_HOURS*2; i++) {
    const h=GS_GRID_START+i*0.5, he=h+0.5;
    let count=(avail[day]||[]).some(([s,e])=>s<=h&&e>=he)?1:0;
    for (const id of ids) { const pAvail=overrides[id]||{}; if((pAvail[day]||[]).some(([s,e])=>s<=h&&e>=he)) count++; }
    counts[i] = count;
  }
  return {counts, total};
}

export function gsFmtH(h) {
  const hr  = Math.floor(h);
  const min = Math.round((h-hr)*60);
  const ms  = min ? `:${String(min).padStart(2,"0")}` : "";
  if (hr===0||hr===24) return `12${ms} AM`;
  if (hr===12)          return `12${ms} PM`;
  return hr<12 ? `${hr}${ms} AM` : `${hr-12}${ms} PM`;
}

export function gsFmtRange(s, e) { return `${gsFmtH(s)} – ${gsFmtH(e)}`; }
export function gsTotalH(overlaps) { return Object.values(overlaps).flat().reduce((s,[a,b])=>s+(b-a),0); }
export function gsHexRgb(hex) { return `${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)}`; }

// ── Calendar colour maps (shared by CalendarContent and HomeContent) ──────────
export const CAL_SOURCE_COLORS = { orbit: T.orbit, apple: "#1C7BE0", ofw: T.coparent };
export const CAL_TYPE_COLORS   = { overnight: T.partner, date: T.partner, social: T.friend, family: T.family, coparent: T.coparent, work: "#5A7BAE", personal: T.amber };

// ── Static demo calendar events (used by CalendarContent) ─────────────────────
export const CAL_EVENTS = [
  {id:"e1", day:0,startH:18,endH:24,label:"Alex overnight",      person:1, source:"orbit", type:"overnight"},
  {id:"e2", day:2,startH:9, endH:11,label:"Priya — coffee",      person:7, source:"orbit", type:"social"},
  {id:"e3", day:3,startH:19,endH:22,label:"Sam date night",      person:3, source:"orbit", type:"date"},
  {id:"e4", day:4,startH:15,endH:16,label:"Devon — handoff",     person:9, source:"orbit", type:"coparent"},
  {id:"e5", day:5,startH:14,endH:24,label:"Alex overnight",      person:1, source:"orbit", type:"overnight"},
  {id:"e6", day:6,startH:17,endH:21,label:"Mom — Sunday dinner", person:5, source:"orbit", type:"family"},
  {id:"x1", day:0,startH:9, endH:10,label:"Team standup",        person:null,source:"apple",type:"work"},
  {id:"x2", day:1,startH:10,endH:11,label:"1:1 with Dana",       person:null,source:"apple",type:"work"},
  {id:"x3", day:1,startH:19,endH:20,label:"Gym",                 person:null,source:"apple",type:"personal"},
  {id:"x4", day:2,startH:10,endH:11,label:"Team standup",        person:null,source:"apple",type:"work"},
  {id:"x5", day:2,startH:14,endH:15,label:"Dentist",             person:null,source:"apple",type:"personal"},
  {id:"x6", day:3,startH:9, endH:10,label:"Team standup",        person:null,source:"apple",type:"work"},
  {id:"x7", day:4,startH:9, endH:10,label:"Team standup",        person:null,source:"apple",type:"work"},
  {id:"x8", day:4,startH:12,endH:13,label:"Lunch w/ Lena",       person:null,source:"apple",type:"social"},
  {id:"x9", day:5,startH:10,endH:12,label:"Farmers market",      person:null,source:"apple",type:"personal"},
  {id:"xa", day:6,startH:10,endH:11,label:"Run — Tilden Park",   person:null,source:"apple",type:"personal"},
  {id:"fw1",day:4,startH:15,endH:16,label:"Custody handoff",     person:9, source:"ofw",   type:"coparent"},
];
